export type SpaceMouseNavigationProfile = 'mouse-only' | 'hybrid'
export type SpaceMouseRotateMode = 'arcball' | 'yaw-pitch'
export type SpaceMouseTargetMode = 'bounds-center' | 'captured-point'
export type SpaceMouseLockValue = 'unlocked' | 'locked'

export interface SpaceMouseNavigationState {
  profile: SpaceMouseNavigationProfile | null
  rotateMode: SpaceMouseRotateMode
  targetMode: SpaceMouseTargetMode
  rotationLocked: boolean
}

export interface SpaceMouseControlOption {
  value: string
  label: string
  detail: string
}

export interface SpaceMouseSecondaryControlGroup {
  id: string
  title: string
  subtitle: string
  options: SpaceMouseControlOption[]
  activeValue: string | null
}

export type SpaceMouseControllerLike = {
  getNavigationProfile?: () => SpaceMouseNavigationProfile | null
  setNavigationProfile?: (profile: SpaceMouseNavigationProfile) => void
  getMouseRotateMode?: () => SpaceMouseRotateMode
  setMouseRotateMode?: (mode: SpaceMouseRotateMode) => void
  getMouseTargetMode?: () => SpaceMouseTargetMode
  setMouseTargetMode?: (mode: SpaceMouseTargetMode) => void
  getRotationLocked?: () => boolean
  setRotationLocked?: (locked: boolean) => void
}

export const SPACEMOUSE_PROFILE_OPTIONS: SpaceMouseControlOption[] = [
  {
    value: 'mouse-only',
    label: '普通鼠标',
    detail: '默认航向/俯仰并优先复用捕捉点旋转',
  },
  {
    value: 'hybrid',
    label: '双鼠标',
    detail: '默认轨迹球并围绕包围盒中心旋转',
  },
]

export const SPACEMOUSE_ROTATE_MODE_OPTIONS: SpaceMouseControlOption[] = [
  {
    value: 'arcball',
    label: '轨迹球',
    detail: '保留对象中心 6DoF 旋转语义',
  },
  {
    value: 'yaw-pitch',
    label: '航向/俯仰',
    detail: '锁定为 yaw/pitch，更接近 Earth 控制器手感',
  },
]

export const SPACEMOUSE_TARGET_MODE_OPTIONS: SpaceMouseControlOption[] = [
  {
    value: 'bounds-center',
    label: '包围盒中心',
    detail: '始终围绕场景包围盒中心旋转',
  },
  {
    value: 'captured-point',
    label: '捕捉点',
    detail: '围绕普通鼠标最近一次有效捕捉点旋转',
  },
]

export const SPACEMOUSE_ROTATION_LOCK_OPTIONS: SpaceMouseControlOption[] = [
  {
    value: 'unlocked',
    label: '允许旋转',
    detail: '普通鼠标和 3D 鼠标都可继续旋转',
  },
  {
    value: 'locked',
    label: '锁定旋转',
    detail: '仅保留平移与缩放，禁止新的朝向变化',
  },
]

export function getSpaceMouseNavigationPreset(
  profile: SpaceMouseNavigationProfile,
): Omit<SpaceMouseNavigationState, 'profile'> {
  return profile === 'mouse-only' ?
      {
        rotateMode: 'yaw-pitch',
        targetMode: 'captured-point',
        rotationLocked: false,
      } :
      {
        rotateMode: 'arcball',
        targetMode: 'bounds-center',
        rotationLocked: false,
      }
}

export function createSpaceMouseNavigationState(
  profile: SpaceMouseNavigationProfile,
): SpaceMouseNavigationState {
  return {
    profile,
    ...getSpaceMouseNavigationPreset(profile),
  }
}

export function deriveSpaceMouseNavigationProfile(
  rotateMode: SpaceMouseRotateMode,
  targetMode: SpaceMouseTargetMode,
  rotationLocked: boolean,
): SpaceMouseNavigationProfile | null {
  if (!rotationLocked) {
    if (rotateMode === 'yaw-pitch' && targetMode === 'captured-point') {
      return 'mouse-only'
    }

    if (rotateMode === 'arcball' && targetMode === 'bounds-center') {
      return 'hybrid'
    }
  }

  return null
}

export function resolveSpaceMouseLockValue(rotationLocked: boolean): SpaceMouseLockValue {
  return rotationLocked ? 'locked' : 'unlocked'
}

export function readSpaceMouseNavigationState(
  controller: SpaceMouseControllerLike | null,
  fallbackProfile: SpaceMouseNavigationProfile,
): SpaceMouseNavigationState {
  const fallbackState = createSpaceMouseNavigationState(fallbackProfile)
  if (!controller) {
    return fallbackState
  }

  const rotateMode = controller.getMouseRotateMode?.() ?? fallbackState.rotateMode
  const targetMode = controller.getMouseTargetMode?.() ?? fallbackState.targetMode
  const rotationLocked = controller.getRotationLocked?.() ?? fallbackState.rotationLocked
  const profile =
    typeof controller.getNavigationProfile === 'function' ?
      controller.getNavigationProfile() :
      deriveSpaceMouseNavigationProfile(rotateMode, targetMode, rotationLocked) ?? fallbackState.profile

  return {
    profile,
    rotateMode,
    targetMode,
    rotationLocked,
  }
}

export function applySpaceMouseNavigationProfile(
  controller: SpaceMouseControllerLike | null,
  profile: SpaceMouseNavigationProfile,
): void {
  if (!controller) {
    return
  }

  const currentState = readSpaceMouseNavigationState(controller, profile)
  const preset = getSpaceMouseNavigationPreset(profile)
  const isPresetAlreadyApplied =
    currentState.profile === profile &&
    currentState.rotateMode === preset.rotateMode &&
    currentState.targetMode === preset.targetMode &&
    currentState.rotationLocked === preset.rotationLocked

  if (isPresetAlreadyApplied) {
    return
  }

  if (typeof controller.setNavigationProfile === 'function') {
    controller.setNavigationProfile(profile)
    return
  }

  controller.setMouseRotateMode?.(preset.rotateMode)
  controller.setMouseTargetMode?.(preset.targetMode)
  controller.setRotationLocked?.(preset.rotationLocked)
}

export function applySpaceMouseSecondaryControlSelection(
  controller: SpaceMouseControllerLike | null,
  groupId: string,
  value: string,
): void {
  if (!controller) {
    return
  }

  if (groupId === 'profile') {
    if (value === 'mouse-only' || value === 'hybrid') {
      applySpaceMouseNavigationProfile(controller, value)
    }
    return
  }

  if (groupId === 'rotate-mode') {
    if (value === 'arcball' || value === 'yaw-pitch') {
      if (controller.getMouseRotateMode?.() !== value) {
        controller.setMouseRotateMode?.(value)
      }
    }
    return
  }

  if (groupId === 'target-mode') {
    if (value === 'bounds-center' || value === 'captured-point') {
      if (controller.getMouseTargetMode?.() !== value) {
        controller.setMouseTargetMode?.(value)
      }
    }
    return
  }

  if (groupId === 'rotation-lock') {
    if (value === 'locked' || value === 'unlocked') {
      const nextLocked = value === 'locked'
      if (controller.getRotationLocked?.() !== nextLocked) {
        controller.setRotationLocked?.(nextLocked)
      }
    }
  }
}

export function buildSpaceMouseSecondaryControlGroups(
  state: SpaceMouseNavigationState,
): SpaceMouseSecondaryControlGroup[] {
  return [
    {
      id: 'profile',
      title: 'SpaceMouse 用户预设',
      subtitle: '在普通鼠标与双鼠标默认行为之间快速切换',
      options: SPACEMOUSE_PROFILE_OPTIONS,
      activeValue: state.profile,
    },
    {
      id: 'rotate-mode',
      title: 'SpaceMouse 旋转模式',
      subtitle: '普通鼠标与 3D 鼠标共用同一套旋转语义',
      options: SPACEMOUSE_ROTATE_MODE_OPTIONS,
      activeValue: state.rotateMode,
    },
    {
      id: 'target-mode',
      title: 'SpaceMouse 目标模式',
      subtitle: '切换包围盒中心与普通鼠标最近一次捕捉点',
      options: SPACEMOUSE_TARGET_MODE_OPTIONS,
      activeValue: state.targetMode,
    },
    {
      id: 'rotation-lock',
      title: 'SpaceMouse 旋转锁定',
      subtitle: '锁定后只保留平移与缩放，不再接受新的旋转',
      options: SPACEMOUSE_ROTATION_LOCK_OPTIONS,
      activeValue: resolveSpaceMouseLockValue(state.rotationLocked),
    },
  ]
}
